<template>
  <div class="section section-details is-paddingless" v-if="commande">
    <div class="entete">
      <div class="level">
        <div class="level-left">
          <div class="columns is-gapless">
            <div class="column">
              <h1 class="title is-size-3">
                {{ $t(`${prefixTrad}.title`, { code: commande.code }) }}
              </h1>
              <app-breadcrumb :breadcrumb="breadcrumb" />
            </div>
          </div>
        </div>
        <div class="level-right">
          <div class="buttons" v-if="!isSansSuite">
            <dropdownButton
              type="is-addOrEdit"
              class="mgr-2"
              v-if="canBeModified"
              :permission="permission.maj"
              :actions="actionSecondary"
              @click="() => {
                $router.push({
                  name: `m.achat.${routeName}`,
                  params: { id: commande.id },
                });
              }">
                {{ $t('detail.buttons.edit') }}&nbsp;
            </dropdownButton>
            <qd-button
              v-if="!isBonReception && allLignesCommandeNonRapprochees"
              :class="{'button-access-no-permission': !checkPermissions(permBonReception.maj)}"
              class="button is-addOrEdit"
              @click="generateBonReception">
              {{ $t(`${prefixTrad}.actions.receptionner`) }}&nbsp;
            </qd-button>
            <qd-button v-if="canBeInvoiced"
              @click="facturation"
              :class="{'button-access-no-permission': !checkPermissions(permission.maj)}"
              class="button is-addOrEdit">
              {{ $t(`${prefixTrad}.facturer`) }}
            </qd-button>
            <button
              v-if="!isBonReception"
              @click="$refs.dropdownEdition.toggle()"
              class="button">
              <div>
              <b-icon icon="file-download" class="mgr-10px" type="is-default"> </b-icon>
              <b-dropdown ref="dropdownEdition" position="is-bottom-left" class="has-text-left">
                <template #trigger>
                  <b-icon icon="chevron-down" class="has-text-secondary-icon"> </b-icon>
                </template>
                <b-dropdown-item @click="checkPermissions(permission.editer)
                  ? editionAction({
                    format: constantes.pdf,
                    typeDonnee: constantes.typeEditionCommande,
                  }) : showPopUp()">
                  {{ $t('general.popUpEdition.type.pdf') }}
                </b-dropdown-item>
                <b-dropdown-item @click="checkPermissions(permission.editer)
                  ? editionAction({
                    format: constantes.docx,
                    typeDonnee: constantes.typeEditionCommande,
                  }) : showPopUp()">
                  {{ $t('general.popUpEdition.type.docx') }}
                </b-dropdown-item>
                <b-dropdown-item @click="checkPermissions(permission.editer)
                  ? openPopUpEdition = true
                  : showPopUp()">
                  {{ $t('general.popUpEdition.type.other') }}
                </b-dropdown-item>
              </b-dropdown>
              </div>
            </button>
            <piece-jointe-button
              :perm="permission.maj"
              class="mgr-2"
              autoSave>
            </piece-jointe-button>
          </div>
        </div>
      </div>
      <div class="tab" v-if="tabs.length > 0">
        <ul>
          <li
            v-for="(tab, index) in tabs" :key="index"
            :class="{'is-active': activeTab === tab}"
            @click="activeTab = tab">
            <b-field>
              <div v-if="tab === 'rapprochementsTab'"
                class="mgv-auto mgr-5px"
                :class="getColorStatusRapprochement"></div>
              <span>{{ $t(`${prefixTrad}.tabs.${tab}`) }}</span>
            </b-field>
          </li>
        </ul>
      </div>
    </div>

    <component
      :is="activeTab"
      :dcf="commande"
      :detailDevisCommande="commandeDetail"
      :devisCommande="commande"
      :commande="commande"
      detail>
    </component>

    <b-modal v-model="popUpSansSuite" has-modal-card class="modal">
      <pop-up-prevention
        :messHeader="$i18n.t('detailCommandeAchatFournisseur.sansSuiteTitle')"
        :cancel="$i18n.t('general.non')"
        :validate="$i18n.t('general.oui')"
        :isErrorPopup.sync='isErrorPopup'
        :messagesPopup='messagesPopup'
        @no="popUpSansSuite = false"
        @yes="sansSuiteCommande()">
      </pop-up-prevention>
    </b-modal>
    <b-modal v-model="popUpSuppression" has-modal-card class="modal">
      <pop-up-prevention
        :messHeader="$t('detailCommandeAchatFournisseur.suppressionTitle')"
        :cancel="$t('general.non')"
        :validate="$t('general.oui')"
        :isErrorPopup="isErrorPopup"
        :messagesPopup="messagesPopup"
        @no="popUpSuppression = false"
        @yes="deleteCommande()"/>
    </b-modal>

    <b-modal v-model="openPopUpEdition" has-modal-card class="modal">
      <pop-up-edition
        :choiceEdition="choiceEdition"
        :error="errorEdition"
        @editionChoose="editionAction">
      </pop-up-edition>
    </b-modal>
  </div>
</template>

<script>
import cloneDeep from 'lodash/cloneDeep';
import { createNamespacedHelpers } from 'vuex';
import edition from '@/api/edition';
import { get, sansSuite, remove } from '@/api/commandeAchat';
import { generateBonReception, get as getBonReception } from '@/api/bonReception';
import { factureGeneration } from '@/api/factureAchatARapprocher';
import PopUpEdition from '@/components/factures/p_edition';
import AppBreadcrumb from '@/components/general/breadcrumb';
import DropdownButton from '@/components/general/button/dropdownButton';
import PieceJointeButton from '@/components/general/button/pieceJointeButton';
import InformationsTab from '@/components/achat/commande/detail/informationsTab';
import RapprochementsTab from '@/components/achat/commande/addEdit/rapprochementsTab';
import LieuDeLivraisonTab from '@/components/achat/commande/detail/lieuDeLivraisonTab';
import messageErreur from '@/services/general/messageErreur';
import validationGeneral from '@/services/validation/validation';
import readErrorBlobRequest from '@/services/edition/readBlobRequest';
import detailDevisCommande from '@/services/devisCommandes/detailDevisCommande';
import validationDevisCommande from '@/services/devisCommandes/validation/validation';
import {
  initializeRapprochementsLignesCommande,
} from '@/services/achat/bonReception/rapprochementServices';
import { TYPE_DOCX, TYPE_PDF } from '@/configuration/edition/editionConf';
import {
  TYPE_EDITION_CMD,
  LISTE_CHOICE_EDITION_COMMANDE,
} from '@/configuration/achat/commandeAchat/commandeAchatConf';
import { ETAT_NON_RAPPROCHE_TYPE, ETAT_RAPPROCHE_TYPE } from '@/configuration/achat/bonReception/rapprochementConfig';
import { COMMANDES, BON_RECEPTION } from '@/configuration/achat/permissionsConf';
import popUpPrevention from '@/components/general/popUpPrevention';
import {
  ETAT_FACTURE,
  ETAT_FACTURE_PARTIELLEMENT,
  ETAT_RAPPROCHE_PARTIELLEMENT,
  ETAT_RAPPROCHE,
  ETAT_SANSSUITE,
  ETAT_ENREGISTRE,
  TYPE_COMMANDE,
} from '@/configuration/devisConf';
import formatIsoDate from '@/services/general/formatIsoDate';
import validationCommandeAchat from '@/services/achat/commandes/validationCommandeAchat';
import messageSucces from '@/services/general/messageSucces';

const Attachments = createNamespacedHelpers('attachments');
const Permissions = createNamespacedHelpers('permissions');

export default {
  name: 'CCommandeAchatFournisseur',
  components: {
    PopUpEdition,
    AppBreadcrumb,
    InformationsTab,
    DropdownButton,
    PieceJointeButton,
    RapprochementsTab,
    popUpPrevention,
    LieuDeLivraisonTab,
  },
  data() {
    return {
      activeTab: 'informationsTab',
      commande: null,
      commandeDetail: null,
      permBonReception: BON_RECEPTION,
      popUpSansSuite: false,
      isErrorPopup: false,
      messagesPopup: [],
      popUpSuppression: false,
      openPopUpEdition: false,
      errorEdition: {
        haveError: false,
        messages: [],
        champsVide: [],
        messagesChampsVide: [],
      },
      constantes: {
        pdf: TYPE_PDF,
        docx: TYPE_DOCX,
        typeEditionCommande: TYPE_EDITION_CMD,
      },
    };
  },
  computed: {
    ...Permissions.mapGetters({
      checkPermissions: 'checkPermissions',
    }),
    isBonReception() {
      return this.$route.name === 'c.achat.bon_reception';
    },
    actionSecondary() {
      const actionSecondary = [];
      if (this.canBePassedToSansSuite) {
        actionSecondary.push({
          label: this.$t('detail.buttons.sansSuite'),
          action: () => { this.openPopUpSansSuite(); },
          order: 1,
        });
      }
      if (this.canBeDeleted) {
        actionSecondary.push({
          label: this.$t('detail.buttons.delete'),
          action: () => { this.openPopUpSuppression(); },
          order: 2,
        });
      }
      return actionSecondary;
    },
    isSansSuite() {
      return this.commande.etat === ETAT_SANSSUITE;
    },
    prefixTrad() {
      return this.isBonReception ? 'detailBonReception' : 'detailCommandeAchatFournisseur';
    },
    routeName() {
      return this.isBonReception ? 'bon_reception' : 'commandes';
    },
    permission() {
      return this.isBonReception ? BON_RECEPTION : COMMANDES;
    },
    tabs() {
      const tabs = [
        { name: 'informationsTab', show: true },
        { name: 'lieuDeLivraisonTab', show: !this.isBonReception },
        { name: 'rapprochementsTab', show: this.isBonReception },
      ];
      return tabs.filter((tab) => tab.show).map((tab) => tab.name);
    },
    breadcrumb() {
      return [
        { title: this.$t('general.breadcrumb.racine'), route: this.$PAGE_DEFAULT, isActive: false },
        { title: this.$t(`${this.prefixTrad}.breadcrumb.liste`), route: { name: `l.achat.${this.routeName}` }, isActive: false },
        { title: this.$t(`${this.prefixTrad}.breadcrumb.title`), route: '', isActive: true },
      ];
    },
    allLignesCommandeNonRapprochees() {
      return this.commande.lignesCommande
        .map(l => l.lieuxLivraisons)
        .flat(2)
        .every((ligne) => ligne.etatRapprochement === ETAT_NON_RAPPROCHE_TYPE);
    },
    allLignesCommandeRapprochees() {
      return this.commande.lignesCommande
        .map(l => l.lieuxLivraisons)
        .flat(2)
        .every((ligne) => ligne.etatRapprochement === ETAT_RAPPROCHE_TYPE);
    },
    getColorStatusRapprochement() {
      switch (this.commande.bonReceptionEtat) {
        case ETAT_RAPPROCHE:
          return 'round-circle-success';
        case ETAT_RAPPROCHE_PARTIELLEMENT:
          return 'round-circle-warning';
        default:
          return 'round-circle-danger';
      }
    },
    canBePassedToSansSuite() {
      return !this.isBonReception && this.allLignesCommandeNonRapprochees && !this.isFacture;
    },
    canBeModified() {
      return this.isBonReception || !this.allLignesCommandeRapprochees;
    },
    canBeDeleted() {
      return !this.isBonReception && (this.isSansSuite || this.commande.etat === ETAT_ENREGISTRE);
    },
    isFacture() {
      return [ETAT_FACTURE, ETAT_FACTURE_PARTIELLEMENT].includes(this.commande.etat);
    },
    canBeInvoiced() {
      return this.commande.echeanciers.some((echeancier) => {
        return echeancier.echeancierLignes.some((ligne) => {
          return ligne.factureId === null && !ligne.facturationFaite;
        });
      });
    },
    choiceEdition() {
      return cloneDeep(LISTE_CHOICE_EDITION_COMMANDE);
    },
  },
  methods: {
    ...Attachments.mapActions({
      setAttachmentContext: 'setContext',
      setAttachmentElementId: 'setElementId',
    }),
    ...Permissions.mapActions({
      showPopUp: 'showPopup',
    }),
    async generateBonReception() {
      const loadingComponent = this.$buefy.loading.open();
      try {
        const response = (await generateBonReception(this.commande.id)).data.result;
        this.$router.push({ name: 'm.achat.bon_reception', params: { id: response.id } });
      } catch (e) {
        messageErreur(this.$i18n);
      } finally {
        loadingComponent.close();
      }
    },
    initialiseListeEcheancePayeurs(echeanciers) {
      const echeancePayeurLigne = [];
      echeanciers.forEach(echeancier => {
        echeancier.echeancierLignes
          .filter(ligneEcheance => ligneEcheance.factureId === null
            && !ligneEcheance.facturationFaite)
          .forEach(ligne => {
            const echeance = {
              id: ligne.id,
            };
            echeancePayeurLigne.push(echeance);
          });
      });
      return echeancePayeurLigne.sort(this.compareEcheance);
    },
    openPopUpSansSuite() {
      this.isErrorPopup = false;
      this.messagesPopup = [];
      this.popUpSansSuite = true;
    },
    openPopUpSuppression() {
      this.isErrorPopup = false;
      this.messagesPopup = [];
      this.popUpSuppression = true;
    },
    async sansSuiteCommande() {
      const loadingComponent = this.$buefy.loading.open();
      try {
        await sansSuite(this.commande.id);
        this.popUpSansSuite = false;
        await this.initializeCommande();
      } catch (e) {
        messageErreur(this.$i18n);
        throw e;
      } finally {
        loadingComponent.close();
      }
    },
    async facturation() {
      const loadingComponent = this.$buefy.loading.open();
      try {
        const echeances = this.initialiseListeEcheancePayeurs(this.commande.echeanciers);
        const facture = (await factureGeneration({
          listeEcheancierLigneId: echeances.map((ech) => ech.id),
          dateFacturation: formatIsoDate(new Date()),
          calculeEnQuantite: true,
          genererFactureZero: true,
          enregistrerFactures: false,
          estARapprocher: true,
        })).data.result[0];
        if (!facture) return;
        this.$router.push({ name: 'm.achat.factures', params: { id: facture.id } });
      } catch (e) {
        messageErreur(this.$i18n);
        throw e;
      } finally {
        loadingComponent.close();
      }
    },
    async editionAction(donnees) {
      const loadingComponent = this.$buefy.loading.open();
      this.errorEdition.champsVide = [];
      this.errorEdition.messagesChampsVide = [];
      this.errorEdition.messages = [];
      this.errorEdition.haveError = false;
      try {
        const response = await edition({
          ids: [this.commande.id],
          extension: donnees.format,
          typeDonnee: donnees.typeDonnee,
          typeEdition: donnees.typeEdition ? donnees.typeEdition : '',
          parametresAdditionnels: {
            ...(donnees.parametresAdditionnels ?? {}),
            societeId: this.commande.societeId,
          },
        });
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(response.data);
        const disposition = response.request.getResponseHeader('Content-Disposition');
        link.download = decodeURI(disposition.match(/filename\*=UTF-8''(.*?)$/)[1]);
        link.click();
        this.openPopUpEdition = false;
      } catch (e) {
        if (e.response) {
          const errorResponseString = await readErrorBlobRequest(e.response.data);
          const data = JSON.parse(errorResponseString);
          validationDevisCommande({ data }, this.errorEdition.messages, null, TYPE_COMMANDE);
          validationGeneral(this.$i18n, { data }, this.errorEdition.champsVide,
            this.errorEdition.messages, this.errorEdition.messagesChampsVide, null);
          this.errorEdition.haveError = true;
          return;
        }
        messageErreur(this.$i18n);
        throw (e);
      } finally {
        loadingComponent.close();
      }
    },
    async initializeCommande() {
      const getFuncAsync = this.isBonReception ? getBonReception : get;
      const commande = (await getFuncAsync(this.$route.params.id)).data.result;
      commande.avoirsAffectes = [];
      commande.echeancierPaiement = [];
      if (this.isBonReception) initializeRapprochementsLignesCommande(commande);
      const commandeDetail = detailDevisCommande(commande);
      this.commande = commande;
      this.commandeDetail = commandeDetail;
    },
    async deleteCommande() {
      const loadingComponent = this.$buefy.loading.open();
      try {
        await remove(this.$route.params.id);
        this.popUpSuppression = false;
        messageSucces(this.$t('detailCommandeAchatFournisseur.suppressionMessageSuccess'));
        await this.$router.push({ name: 'l.achat.commandes' });
      } catch (e) {
        this.isErrorPopup = true;
        if (e.response?.data?.error) {
          validationCommandeAchat(e.response, this.messagesPopup);
        } else messageErreur(this.$i18n);
        throw e;
      } finally {
        loadingComponent.close();
      }
    },
  },
  async mounted() {
    const loadingComponent = this.$buefy.loading.open();
    try {
      await this.initializeCommande();
      await this.setAttachmentContext({ module: 'Commande' });
      await this.setAttachmentElementId({ id: this.commande.id });
      this.setInformationPage(this.$t('pageGeneral.detail', { item: this.commande.code }));
      loadingComponent.close();
    } catch (e) {
      messageErreur(this.$i18n);
      throw (e);
    } finally {
      loadingComponent.close();
    }
  },
};
</script>
